import "./styles/Navbar.scss";
import Navbar from "./components/Navbar";
import "@fortawesome/fontawesome-free/css/all.css";

import Home from "./pages/Home";
import ModelsPage from "./pages/ModelsPage";
import ModelDetailsPage from "./pages/ModelDetailsPage";
import ProjectsPage from "./pages/ProjectPage";
import ProjectDetailsPage from "./pages/ProjectDetailsPage";
import ContactPage from "./pages/ContactPage";
import GetScoutedPage from "./pages/GetScoutedPage";
import ComingSoon from "./pages/ComingSoon";

import AdminUploadPage from "./admin/AdminUploadPage";
import AdminReviewPage from "./admin/AdminReviewPage";
import AdminDashboardPage from "./admin/AdminDashboard";
import AdminAddModel from "./admin/AdminAddModel";
import LoginPage from "./admin/LoginPage";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, type User } from "firebase/auth";
import { AnimatePresence } from "framer-motion";
import Preloader from "./components/Preloader";
import AdminEditModelPage from "./admin/AdminEditModelPage";
import AdminModelsList from "./admin/AdminModelsList";

type AppContentProps = {
  currentUser: User | null;
};

const AppContent = ({ currentUser }: AppContentProps) => {
  const location = useLocation();

  // Admin pages (hide Navbar on these)
  // const adminRoutes = [
  //   "/admin-login",
  //   "/admin-upload",
  //   "/admin-review",
  //   "/admin-dashboard",
  //   "/admin/add-model",
  // ];
  // const isAdminRoute = adminRoutes.includes(location.pathname);
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {!isAdminRoute && <Navbar />}

      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/models" element={<ModelsPage />} />
          <Route path="/model/:id" element={<ModelDetailsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/projects/:eventId" element={<ProjectDetailsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/get-scouted" element={<GetScoutedPage />} />

          {/* Admin */}
          <Route
            path="/admin-login"
            element={currentUser ? <Navigate to="/admin-dashboard" replace /> : <LoginPage />}
          />
          <Route
            path="/admin-upload"
            element={currentUser ? <AdminUploadPage /> : <Navigate to="/admin-login" replace />}
          />
          <Route
            path="/admin-dashboard"
            element={currentUser ? <AdminDashboardPage /> : <Navigate to="/admin-login" replace />}
          />
          <Route
            path="/admin-review"
            element={currentUser ? <AdminReviewPage /> : <Navigate to="/admin-login" replace />}
          />
          <Route
            path="/admin/add-model"
            element={currentUser ? <AdminAddModel /> : <Navigate to="/admin-login" replace />}
          />
          <Route
           path="/admin/models"
           element={currentUser ? <AdminModelsList /> : <Navigate to="/admin-login" replace />}
          />
          <Route
             path="/admin/models/:id/edit"
             element={currentUser ? <AdminEditModelPage /> : <Navigate to="/admin-login" replace />}
          />
          {/* Fallback */}
          <Route path="*" element={<ComingSoon />} />
        </Routes>
      </AnimatePresence>
    </>
  );
};

export default function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [loadingPage, setLoadingPage] = useState(true);
  const [fadeOutPreloader, setFadeOutPreloader] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      setCurrentUser(user);
      setLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  // Simple preloader flow
  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeOutPreloader(true);
      setTimeout(() => setLoadingPage(false), 800);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  if (loadingPage || loadingAuth) {
    return <Preloader fadeOut={fadeOutPreloader} />;
  }

  return (
    <Router>
      <AppContent currentUser={currentUser} />
    </Router>
  );
}
